/**
 * Interactive editor tutorial: a floating step card that watches real editor
 * state (placed objects, mode, selection, playtest) and advances as the user
 * actually performs each action — no fake "next" buttons. Auto-starts on the
 * first editor open; restartable any time from the topbar.
 */
import type { LevelData } from '../format/types';
import { t } from '../ui/strings';

const DONE_KEY = 'shape-shifters.editor-tutorial.v1';

/** Counts-only view of editor state: the store mutates the level in place,
 * so baselines must copy numbers, never hold the live object graph. */
export interface TutorialSnapshot {
  blocks: number;
  spikes: number;
  triggers: number;
  mode: 'build' | 'edit' | 'delete';
  selectionSize: number;
  /** True once the user has launched a playtest during this tutorial run. */
  playtested: boolean;
}

export function snapshotCounts(level: LevelData): {
  blocks: number;
  spikes: number;
  triggers: number;
} {
  let blocks = 0;
  let spikes = 0;
  let triggers = 0;
  for (const o of level.objects) {
    if (o.type === 'block') blocks++;
    else if (o.type === 'spike') spikes++;
    else if (o.type === 'trigger') triggers++;
  }
  return { blocks, spikes, triggers };
}

interface Step {
  /** i18n keys for the card title/body. */
  title: string;
  body: string;
  done(s: TutorialSnapshot, baseline: TutorialSnapshot): boolean;
}

export const TUTORIAL_STEPS: readonly Step[] = [
  {
    title: 'ed.tut.1.title',
    body: 'ed.tut.1.body',
    done: (s, b) => s.blocks > b.blocks,
  },
  {
    title: 'ed.tut.2.title',
    body: 'ed.tut.2.body',
    done: (s, b) => s.spikes > b.spikes,
  },
  {
    title: 'ed.tut.3.title',
    body: 'ed.tut.3.body',
    done: (s) => s.mode === 'edit' && s.selectionSize > 0,
  },
  {
    title: 'ed.tut.4.title',
    body: 'ed.tut.4.body',
    done: (s, b) => s.triggers > b.triggers,
  },
  {
    title: 'ed.tut.5.title',
    body: 'ed.tut.5.body',
    done: (s) => s.playtested,
  },
];

export function tutorialCompleted(): boolean {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem(DONE_KEY) !== null;
}

function markCompleted(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(DONE_KEY, new Date().toISOString());
  } catch {
    /* storage unavailable — tutorial will simply offer again next time */
  }
}

export class EditorTutorial {
  private host: HTMLElement;
  private card: HTMLElement | null = null;
  private step = 0;
  private baseline: TutorialSnapshot | null = null;
  playtested = false;

  constructor(
    host: HTMLElement,
    private readonly snapshot: () => Omit<TutorialSnapshot, 'playtested'>,
  ) {
    this.host = host;
  }

  get active(): boolean {
    return this.baseline !== null;
  }

  start(): void {
    this.step = 0;
    this.playtested = false;
    this.baseline = { ...this.snapshot(), playtested: false };
    this.renderCard();
  }

  stop(markDone: boolean): void {
    this.baseline = null;
    this.card?.remove();
    this.card = null;
    if (markDone) markCompleted();
  }

  /** Call whenever editor state may have changed (store/mode/selection/playtest). */
  notify(): void {
    if (!this.baseline) return;
    const snap: TutorialSnapshot = { ...this.snapshot(), playtested: this.playtested };
    while (
      this.step < TUTORIAL_STEPS.length &&
      (TUTORIAL_STEPS[this.step] as Step).done(snap, this.baseline)
    ) {
      this.step++;
    }
    if (this.step >= TUTORIAL_STEPS.length) {
      this.renderComplete();
      return;
    }
    this.renderCard();
  }

  private renderCard(): void {
    const def = TUTORIAL_STEPS[this.step] as Step;
    this.ensureCard();
    (this.card as HTMLElement).innerHTML = `
      <div class="ed-tut-head">
        <b>${t('ed.tut.progress', { n: this.step + 1, total: TUTORIAL_STEPS.length })}</b>
        <button class="ed-tut-close" title="${t('ed.tut.skip')}">✕</button>
      </div>
      <div class="ed-tut-title">${t(def.title)}</div>
      <div class="ed-tut-body">${t(def.body)}</div>`;
    this.bindClose();
  }

  private renderComplete(): void {
    this.ensureCard();
    (this.card as HTMLElement).innerHTML = `
      <div class="ed-tut-head"><b>${t('ed.tut.done.title')}</b>
        <button class="ed-tut-close" title="${t('ed.tut.skip')}">✕</button></div>
      <div class="ed-tut-body">${t('ed.tut.done.body')}</div>`;
    this.bindClose();
    this.baseline = null; // finished watching; card stays until dismissed
    markCompleted();
  }

  private ensureCard(): void {
    if (this.card) return;
    this.card = document.createElement('div');
    this.card.id = 'ed-tutorial';
    this.host.appendChild(this.card);
  }

  private bindClose(): void {
    this.card?.querySelector('.ed-tut-close')?.addEventListener('click', () => this.stop(true));
  }
}
